/**
 * Melhoria 4 (2026-06-17): CRUD de Clientes e Contratos.
 *
 * Painel usado dentro da tela "Novo Cadastro" para que a Diretora/Secretária
 * possa listar, editar e excluir cadastros existentes, com preview de
 * impacto (dados relacionados) antes da exclusão.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Building2, FileText, Pencil, Trash2, Search, AlertTriangle,
  Loader2, X, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatDateBR, formatCurrencyBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const CLIENT_TYPES = [
  { value: "prefeitura", label: "Prefeitura" },
  { value: "camara", label: "Câmara Municipal" },
  { value: "outro", label: "Outro" },
];

// Labels amigáveis para os contadores retornados pelo delete-preview.
const CASCADE_LABELS = {
  contracts: "Contratos",
  meetings: "Reuniões",
  documents: "Documentos",
  providencias: "Providências",
  directives: "Diretrizes",
  reports: "Relatórios mensais",
  activity_logs: "Registros de histórico",
  linked_client_users: "Usuários cliente vinculados",
  ratings: "Avaliações",
  orphan_client_users: "Usuários cliente que ficarão sem contrato",
};

function statusBadge(status) {
  const map = {
    pending_assignment: { label: "Aguardando designação", color: "bg-amber-100 text-amber-800" },
    pending_director_approval: { label: "Aguardando Diretora", color: "bg-sky-100 text-sky-800" },
    active: { label: "Ativo", color: "bg-emerald-100 text-emerald-800" },
    completed: { label: "Concluído", color: "bg-slate-100 text-slate-700" },
    canceled: { label: "Cancelado", color: "bg-rose-100 text-rose-800" },
  };
  const s = map[status] || { label: status || "—", color: "bg-slate-100 text-slate-700" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${s.color}`}>{s.label}</span>
  );
}

// ===========================================================================
// Client edit dialog
// ===========================================================================
function ClientEditDialog({ open, client, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || "",
        type: client.type || "prefeitura",
        city: client.city || "",
        state: client.state || "",
        cnpj: client.cnpj || "",
        contact_name: client.contact_name || "",
        contact_email: client.contact_email || "",
        contact_phone: client.contact_phone || "",
      });
    }
  }, [client]);

  const save = async () => {
    if (!client) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Remove strings vazias para não sobrescrever com "" no backend
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      const { data } = await api.put(`/clients/${client.id}`, payload);
      toast.success("Cliente atualizado.");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar cliente.");
    } finally {
      setSaving(false);
    }
  };

  if (!client) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="client-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
          <DialogDescription>
            Ajuste os dados cadastrais do cliente <strong>{client.name}</strong>.
            O contrato assinado (arquivo original) não é alterado aqui.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Nome</Label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1" data-testid="edit-client-name" />
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="mt-1" data-testid="edit-client-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>CNPJ</Label>
            <Input value={form.cnpj || ""} onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
              className="mt-1" data-testid="edit-client-cnpj" />
          </div>
          <div>
            <Label>Cidade</Label>
            <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="mt-1" data-testid="edit-client-city" />
          </div>
          <div>
            <Label>UF</Label>
            <Input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
              maxLength={2} className="mt-1" data-testid="edit-client-state" />
          </div>
          <div>
            <Label>Contato (nome)</Label>
            <Input value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className="mt-1" data-testid="edit-client-contact-name" />
          </div>
          <div>
            <Label>Contato (e-mail)</Label>
            <Input type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="mt-1" data-testid="edit-client-contact-email" />
          </div>
          <div className="sm:col-span-2">
            <Label>Contato (telefone)</Label>
            <Input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="mt-1" data-testid="edit-client-contact-phone" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-client-cancel">
            Cancelar
          </Button>
          <Button
            className="bg-midnight hover:bg-ink text-white"
            onClick={save}
            disabled={saving}
            data-testid="edit-client-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Client delete dialog with cascade preview
// ===========================================================================
function ClientDeleteDialog({ open, client, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open || !client) {
      setPreview(null);
      setConfirmText("");
      return;
    }
    setLoading(true);
    api.get(`/clients/${client.id}/delete-preview`)
      .then(({ data }) => setPreview(data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Erro ao carregar preview.");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, client]);

  const hasChildren = preview && Object.values(preview.counts || {}).some((v) => v > 0);
  const requiredText = client?.name || "";
  const canDelete = confirmText.trim() === requiredText;

  const doDelete = async () => {
    if (!client) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${client.id}${hasChildren ? "?force=true" : ""}`);
      toast.success(`Cliente "${client.name}" excluído.`);
      onDeleted(client.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir cliente.");
    } finally {
      setDeleting(false);
    }
  };

  if (!client) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="client-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> Excluir cliente
          </DialogTitle>
          <DialogDescription>
            Você está prestes a excluir <strong>{client.name}</strong>.
            Esta ação é <strong>irreversível</strong>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando impacto…
          </div>
        ) : preview ? (
          <div className="space-y-3">
            {hasChildren ? (
              <div className="p-3 border border-amber-300 bg-amber-50 rounded-md text-sm">
                <div className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Este cliente possui dados vinculados. Todos serão apagados em cascata:
                </div>
                <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 list-disc list-inside text-amber-900">
                  {Object.entries(preview.counts).filter(([, v]) => v > 0).map(([k, v]) => (
                    <li key={k}>
                      <strong>{v}</strong> {CASCADE_LABELS[k] || k}
                    </li>
                  ))}
                </ul>
                {preview.orphan_client_users?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-900">
                    <strong>Usuários cliente afetados:</strong> {preview.orphan_client_users.map((u) => u.name || u.email).join(", ")}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-3 border border-slate-200 bg-slate-50 rounded-md text-sm text-slate-700">
                Este cliente não possui dados vinculados. Exclusão simples.
              </div>
            )}

            <div>
              <Label>Digite <strong>{requiredText}</strong> para confirmar</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-1"
                placeholder={requiredText}
                data-testid="client-delete-confirm-input"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting} data-testid="client-delete-cancel">
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={doDelete}
            disabled={!canDelete || deleting}
            data-testid="client-delete-confirm"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Excluir definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Contract edit dialog
// ===========================================================================
function ContractEditDialog({ open, contract, clients, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [consultants, setConsultants] = useState([]);

  useEffect(() => {
    if (contract) {
      setForm({
        client_id: contract.client_id || "",
        start_date: (contract.start_date || "").slice(0, 10),
        end_date: (contract.end_date || "").slice(0, 10),
        contract_value: contract.contract_value ?? "",
        notes: contract.notes || "",
        consultant_id: contract.consultant_id || "",
      });
    }
  }, [contract]);

  useEffect(() => {
    if (open) {
      api.get("/users?role=CONSULTOR")
        .then(({ data }) => setConsultants(data))
        .catch(() => {});
    }
  }, [open]);

  const save = async () => {
    if (!contract) return;
    setSaving(true);
    try {
      const payload = {};
      if (form.client_id && form.client_id !== contract.client_id) {
        payload.client_id = form.client_id;
      }
      if (form.start_date) payload.start_date = new Date(form.start_date).toISOString();
      if (form.end_date) payload.end_date = new Date(form.end_date).toISOString();
      if (form.contract_value !== "" && form.contract_value !== null) {
        payload.contract_value = Number(form.contract_value);
      }
      if (form.notes !== undefined) payload.notes = form.notes;
      if (form.consultant_id && form.consultant_id !== contract.consultant_id) {
        payload.consultant_id = form.consultant_id;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("Nenhuma alteração para salvar.");
        return;
      }
      const { data } = await api.put(`/contracts/${contract.id}`, payload);
      toast.success("Contrato atualizado.");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar contrato.");
    } finally {
      setSaving(false);
    }
  };

  if (!contract) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="contract-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar contrato</DialogTitle>
          <DialogDescription>
            Ajuste os dados básicos do contrato.
            O produto contratado <strong>não pode</strong> ser alterado para preservar o histórico de fases.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Cliente</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger className="mt-1" data-testid="edit-contract-client">
                <SelectValue placeholder="Selecione o cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} — {c.city}/{c.state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Data de início</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="mt-1" data-testid="edit-contract-start" />
          </div>
          <div>
            <Label>Data de término</Label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="mt-1" data-testid="edit-contract-end" />
          </div>
          <div className="sm:col-span-2">
            <Label>Valor do contrato (R$)</Label>
            <Input type="number" step="0.01" value={form.contract_value}
              onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
              className="mt-1" data-testid="edit-contract-value" />
          </div>
          <div className="sm:col-span-2">
            <Label>Consultor responsável (principal)</Label>
            <Select value={form.consultant_id} onValueChange={(v) => setForm({ ...form, consultant_id: v })}>
              <SelectTrigger className="mt-1" data-testid="edit-contract-consultant">
                <SelectValue placeholder="Selecione o consultor…" />
              </SelectTrigger>
              <SelectContent>
                {consultants.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Para adicionar consultores adicionais, use a aba <strong>Usuários</strong> dentro da tela do contrato.
            </p>
          </div>
          <div className="sm:col-span-2">
            <Label>Observações</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3} className="mt-1" data-testid="edit-contract-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            className="bg-midnight hover:bg-ink text-white"
            onClick={save}
            disabled={saving}
            data-testid="edit-contract-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Contract delete dialog with cascade preview
// ===========================================================================
function ContractDeleteDialog({ open, contract, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open || !contract) {
      setPreview(null);
      setConfirmText("");
      return;
    }
    setLoading(true);
    api.get(`/contracts/${contract.id}/delete-preview`)
      .then(({ data }) => setPreview(data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Erro ao carregar preview.");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, contract]);

  const hasChildren = preview && Object.values(preview.counts || {}).some((v) => v > 0);
  const requiredText = "EXCLUIR";
  const canDelete = confirmText.trim() === requiredText;

  const doDelete = async () => {
    if (!contract) return;
    setDeleting(true);
    try {
      await api.delete(`/contracts/${contract.id}`);
      toast.success("Contrato excluído.");
      onDeleted(contract.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir contrato.");
    } finally {
      setDeleting(false);
    }
  };

  if (!contract) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="contract-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> Excluir contrato
          </DialogTitle>
          <DialogDescription>
            Excluir o contrato de <strong>{contract.client_name || "—"}</strong> (produto: <strong>{contract.product_name || "—"}</strong>).
            Esta ação é <strong>irreversível</strong>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando impacto…
          </div>
        ) : preview ? (
          <div className="space-y-3">
            {hasChildren ? (
              <div className="p-3 border border-amber-300 bg-amber-50 rounded-md text-sm">
                <div className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Este contrato possui dados vinculados que serão excluídos em cascata:
                </div>
                <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 list-disc list-inside text-amber-900">
                  {Object.entries(preview.counts).filter(([, v]) => v > 0).map(([k, v]) => (
                    <li key={k}>
                      <strong>{v}</strong> {CASCADE_LABELS[k] || k}
                    </li>
                  ))}
                </ul>
                {preview.orphan_client_users?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-900">
                    <strong>Usuários cliente que ficarão sem contrato:</strong>{" "}
                    {preview.orphan_client_users.map((u) => u.name || u.email).join(", ")}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-3 border border-slate-200 bg-slate-50 rounded-md text-sm text-slate-700">
                Este contrato não possui dados vinculados. Exclusão simples.
              </div>
            )}

            <div>
              <Label>Digite <strong>{requiredText}</strong> para confirmar</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-1"
                placeholder={requiredText}
                data-testid="contract-delete-confirm-input"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancelar</Button>
          <Button
            variant="destructive"
            onClick={doDelete}
            disabled={!canDelete || deleting}
            data-testid="contract-delete-confirm"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Excluir definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Main component
// ===========================================================================
export const CadastrosManager = ({ initialClients = [], onClientsChanged }) => {
  const { user } = useAuth();
  const isDiretora = user?.role === ROLES.DIRETORA;
  const [subTab, setSubTab] = useState("clients");
  const [clients, setClients] = useState(initialClients);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Dialogs
  const [editClient, setEditClient] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);
  const [editContract, setEditContract] = useState(null);
  const [deleteContract, setDeleteContract] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: cs }, { data: ct }] = await Promise.all([
        api.get("/clients"),
        api.get("/contracts"),
      ]);
      setClients(cs);
      setContracts(ct);
      onClientsChanged?.(cs);
    } catch (err) {
      toast.error("Erro ao recarregar cadastros.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.name, c.city, c.state, c.cnpj, c.contact_email, c.contact_name]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    );
  }, [clients, search]);

  const filteredContracts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contracts;
    return contracts.filter((c) =>
      [c.client_name, c.product_name, c.status, c.consultant_name]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    );
  }, [contracts, search]);

  const contractsPerClient = useMemo(() => {
    const map = new Map();
    contracts.forEach((c) => {
      const arr = map.get(c.client_id) || [];
      arr.push(c);
      map.set(c.client_id, arr);
    });
    return map;
  }, [contracts]);

  return (
    <Card className="card-soft" data-testid="cadastros-manager">
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-midnight" />
          Cadastros existentes
        </CardTitle>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, cidade, CNPJ…"
              className="pl-8 w-64"
              data-testid="cadastros-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="cadastros-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Recarregar"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="clients" data-testid="sub-tab-clients">
              <Building2 className="w-4 h-4 mr-2" /> Clientes ({filteredClients.length})
            </TabsTrigger>
            <TabsTrigger value="contracts" data-testid="sub-tab-contracts">
              <FileText className="w-4 h-4 mr-2" /> Contratos ({filteredContracts.length})
            </TabsTrigger>
          </TabsList>

          {/* -------- Clientes -------- */}
          <TabsContent value="clients">
            {filteredClients.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                {search ? "Nenhum cliente encontrado para o filtro." : "Nenhum cliente cadastrado."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-left text-xs text-slate-600 uppercase">
                    <tr>
                      <th className="py-2 pr-4">Nome</th>
                      <th className="py-2 pr-4">Cidade / UF</th>
                      <th className="py-2 pr-4">CNPJ</th>
                      <th className="py-2 pr-4">Contato</th>
                      <th className="py-2 pr-4"># Contratos</th>
                      <th className="py-2 pr-4 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((c) => {
                      const ctCount = (contractsPerClient.get(c.id) || []).length;
                      return (
                        <tr
                          key={c.id}
                          className="border-b border-slate-100 hover:bg-slate-50"
                          data-testid={`client-row-${c.id}`}
                        >
                          <td className="py-3 pr-4">
                            <div className="font-medium text-slate-900">{c.name}</div>
                            {c.type && <div className="text-xs text-slate-500">{c.type}</div>}
                          </td>
                          <td className="py-3 pr-4">{c.city} / {c.state}</td>
                          <td className="py-3 pr-4 text-xs text-slate-700">{c.cnpj || "—"}</td>
                          <td className="py-3 pr-4 text-xs">
                            <div>{c.contact_name || "—"}</div>
                            <div className="text-slate-500">{c.contact_email || "—"}</div>
                          </td>
                          <td className="py-3 pr-4">
                            <Badge variant="secondary">{ctCount}</Badge>
                          </td>
                          <td className="py-3 pr-4 text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditClient(c)}
                                data-testid={`edit-client-${c.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                              </Button>
                              {isDiretora && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-700 border-red-200 hover:bg-red-50"
                                  onClick={() => setDeleteClient(c)}
                                  data-testid={`delete-client-${c.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* -------- Contratos -------- */}
          <TabsContent value="contracts">
            {filteredContracts.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                {search ? "Nenhum contrato encontrado para o filtro." : "Nenhum contrato cadastrado."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-left text-xs text-slate-600 uppercase">
                    <tr>
                      <th className="py-2 pr-4">Cliente</th>
                      <th className="py-2 pr-4">Produto</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Período</th>
                      <th className="py-2 pr-4">Valor</th>
                      <th className="py-2 pr-4">Progresso</th>
                      <th className="py-2 pr-4 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContracts.map((c) => (
                      <tr
                        key={c.id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`contract-row-${c.id}`}
                      >
                        <td className="py-3 pr-4 font-medium text-slate-900">{c.client_name || "—"}</td>
                        <td className="py-3 pr-4">{c.product_name || "—"}</td>
                        <td className="py-3 pr-4">{statusBadge(c.status)}</td>
                        <td className="py-3 pr-4 text-xs">
                          {formatDateBR(c.start_date)} → {formatDateBR(c.end_date)}
                        </td>
                        <td className="py-3 pr-4 text-xs">
                          {c.contract_value ? formatCurrencyBR(c.contract_value) : "—"}
                        </td>
                        <td className="py-3 pr-4 text-xs">{c.progress_pct ?? 0}%</td>
                        <td className="py-3 pr-4 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditContract(c)}
                              data-testid={`edit-contract-${c.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                            </Button>
                            {isDiretora && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-700 border-red-200 hover:bg-red-50"
                                onClick={() => setDeleteContract(c)}
                                data-testid={`delete-contract-${c.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <ClientEditDialog
        open={!!editClient}
        client={editClient}
        onClose={() => setEditClient(null)}
        onSaved={(updated) => {
          setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
          onClientsChanged?.(clients.map((c) => (c.id === updated.id ? updated : c)));
        }}
      />
      <ClientDeleteDialog
        open={!!deleteClient}
        client={deleteClient}
        onClose={() => setDeleteClient(null)}
        onDeleted={(cid) => {
          setClients((prev) => prev.filter((c) => c.id !== cid));
          setContracts((prev) => prev.filter((ct) => ct.client_id !== cid));
          onClientsChanged?.(clients.filter((c) => c.id !== cid));
        }}
      />
      <ContractEditDialog
        open={!!editContract}
        contract={editContract}
        clients={clients}
        onClose={() => setEditContract(null)}
        onSaved={(updated) => {
          setContracts((prev) => prev.map((ct) => (ct.id === updated.id ? updated : ct)));
        }}
      />
      <ContractDeleteDialog
        open={!!deleteContract}
        contract={deleteContract}
        onClose={() => setDeleteContract(null)}
        onDeleted={(cid) => {
          setContracts((prev) => prev.filter((ct) => ct.id !== cid));
        }}
      />
    </Card>
  );
};

export default CadastrosManager;
